import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../services/api';
import { ChartSkeleton } from '../../components/skeletons';
import { useAuth } from '../../context/AuthContext';

const GREEN = '#1B5C3A';
const GOLD = '#F5A623';
const PURPLE = '#4C1D95';
const BLUE = '#2563eb';
const RED = '#dc2626';
const CREAM = '#FAF9F6';
const CHAR = '#1a1a1a';
const MUTED = '#6b7280';
const BORDER = '#e5e7eb';

export default function AdminAnalytics() {
  useAuth();
  const [analyticsData, setAnalyticsData] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    Promise.all([
      adminAPI.analytics(),
      adminAPI.dashboard()
    ])
      .then(([analyticsRes, dashboardRes]) => {
        setAnalyticsData(analyticsRes.data.data || analyticsRes.data);
        setDashboardData(dashboardRes.data.data || dashboardRes.data);
      })
      .catch(e => setError(e.response?.data?.message || 'Failed to load analytics'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <ChartSkeleton height={320} style={{ margin: '24px 20px' }} />;

  if (error) return (
    <div style={{ padding: 24 }}>
      <div className="card" style={{ padding: 24, borderLeft: '4px solid #dc2626', color: '#dc2626' }}>{error}</div>
    </div>
  );

  const monthlyRevenue = analyticsData?.monthly || [];
  const topCategories = analyticsData?.topCategories || [];
  const maxRevenue = Math.max(...monthlyRevenue.map(m => m.revenue || 0), 1);
  const maxCatVolume = Math.max(...topCategories.map(c => c.count || 0), 1);

  const dash = dashboardData || {};
  
  // Stats
  const stats = [
    { label: 'Total Revenue (MTD)', value: `₹${(dash.mtdRevenue || 0).toLocaleString('en-IN')}`, color: GREEN, icon: '💰', sub: `${dash.mtdLeads || 0} leads sold` },
    { label: 'Total Demands', value: dash.totalDemands || 0, color: PURPLE, icon: '📋', sub: `${dash.activeDemands || 0} active` },
    { label: 'Total Vendors', value: dash.totalVendors || 0, color: GOLD, icon: '🏪', sub: `${dash.activeVendors || 0} active` },
    { label: 'Wallet Funds Pool', value: `₹${(dash.totalWalletFunds || 0).toLocaleString('en-IN')}`, color: BLUE, icon: '🏦', sub: `${dash.lowBalance || 0} low balance, ${dash.pausedVendors || 0} paused` },
  ];

  return (
    <div style={{ padding: '32px 40px', background: CREAM, minHeight: '100vh' }}>
      <style>{`
        .hover-card {
          transition: transform 0.3s ease, box-shadow 0.3s ease;
          background: white;
          border-radius: 12px;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03);
          border: 1px solid ${BORDER};
        }
        .hover-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
        }
        .bar-chart-bar {
          transition: height 0.8s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .bar-chart-container:hover .bar-chart-bar {
          opacity: 0.8;
        }
        .bar-chart-container .bar-chart-bar:hover {
          opacity: 1;
          filter: brightness(1.1);
        }
      `}</style>

      <div style={{ marginBottom: 32, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
        <div>
          <h1 style={{ fontFamily: 'var(--font-serif)', fontSize: 32, fontWeight: 800, color: CHAR, margin: 0, letterSpacing: '-0.02em' }}>Platform Analytics</h1>
          <p style={{ color: MUTED, fontSize: 15, marginTop: 6, fontWeight: 500 }}>Comprehensive real-time performance insights and revenue tracking.</p>
        </div>
        <div style={{ padding: '8px 16px', background: 'rgba(27, 92, 58, 0.1)', color: GREEN, borderRadius: 20, fontSize: 13, fontWeight: 600 }}>
          Live Data Active
        </div>
      </div>

      {/* KPI Stats Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 20, marginBottom: 32 }}>
        {stats.map((s, i) => (
          <div key={i} className="hover-card" style={{ padding: 24, borderTop: `4px solid ${s.color}`, position: 'relative', overflow: 'hidden' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div style={{ zIndex: 1 }}>
                <div style={{ fontSize: 12, color: MUTED, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 8, fontWeight: 600 }}>{s.label}</div>
                <div style={{ fontFamily: 'var(--font-serif)', fontSize: 32, fontWeight: 700, color: CHAR, marginBottom: 4 }}>{s.value}</div>
                <div style={{ fontSize: 13, color: s.color, fontWeight: 500 }}>{s.sub}</div>
              </div>
              <div style={{ fontSize: 36, opacity: 0.15, position: 'absolute', right: 20, top: 24, zIndex: 0 }}>{s.icon}</div>
            </div>
          </div>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 24, marginBottom: 24 }}>
        {/* Monthly Revenue Bar Chart */}
        <div className="hover-card" style={{ padding: 32 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 32 }}>
            <h3 style={{ fontFamily: 'var(--font-serif)', fontSize: 20, fontWeight: 700, color: CHAR, margin: 0 }}>
              Revenue Trend (Last 6 Months)
            </h3>
            <span style={{ fontSize: 12, color: MUTED, fontWeight: 500, padding: '4px 12px', background: '#f3f4f6', borderRadius: 12 }}>
              Lead Deductions
            </span>
          </div>

          {monthlyRevenue.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 20px', color: MUTED }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>📈</div>
              <p style={{ fontSize: 15, fontWeight: 500 }}>No revenue data available yet</p>
            </div>
          ) : (
            <div className="bar-chart-container">
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: 16, height: 260, padding: '0 8px' }}>
                {monthlyRevenue.map((m, i) => {
                  const val = m.revenue || 0;
                  const leads = m.leads || 0;
                  const pct = Math.max((val / maxRevenue) * 100, 2); // Minimum height for visibility
                  const label = m.month || `M${i + 1}`;
                  
                  return (
                    <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', opacity: val > 0 ? 1 : 0.5 }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: CHAR }}>
                          ₹{val >= 1000 ? `${(val / 1000).toFixed(1)}K` : val}
                        </div>
                        <div style={{ fontSize: 10, color: MUTED }}>
                          {leads} leads
                        </div>
                      </div>
                      <div className="bar-chart-bar" style={{
                        width: '100%', maxWidth: 64,
                        height: `${pct}%`,
                        background: `linear-gradient(180deg, ${GREEN} 0%, ${GREEN}dd 100%)`,
                        borderRadius: '8px 8px 0 0',
                        boxShadow: '0 4px 12px rgba(27, 92, 58, 0.2)',
                        cursor: 'pointer'
                      }} title={`${label}: ₹${val} (${leads} leads)`} />
                      <div style={{ fontSize: 13, color: CHAR, fontWeight: 600 }}>{label}</div>
                    </div>
                  );
                })}
              </div>
              <div style={{ borderTop: `2px solid ${BORDER}`, marginTop: 8 }} />
            </div>
          )}
        </div>

        {/* Top Categories by Demand Volume */}
        <div className="hover-card" style={{ padding: 32 }}>
          <h3 style={{ fontFamily: 'var(--font-serif)', fontSize: 20, fontWeight: 700, color: CHAR, margin: 0, marginBottom: 4 }}>
            Top Categories
          </h3>
          <p style={{ fontSize: 13, color: MUTED, marginBottom: 24, fontWeight: 500 }}>By overall demand volume</p>

          {topCategories.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 16px', color: MUTED }}>
              <div style={{ fontSize: 32, marginBottom: 12 }}>📦</div>
              <p style={{ fontSize: 14, fontWeight: 500 }}>No category data yet</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              {topCategories.slice(0, 6).map((cat, i) => {
                const vol = cat.count || 0;
                const pct = (vol / maxCatVolume) * 100;
                const colors = [GREEN, PURPLE, GOLD, BLUE, RED, '#059669'];
                const c = colors[i % colors.length];
                return (
                  <div key={i}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                      <span style={{ fontSize: 14, fontWeight: 600, color: CHAR }}>
                        {cat.name || 'Unknown'}
                      </span>
                      <span style={{ fontSize: 14, fontWeight: 700, color: c }}>{vol}</span>
                    </div>
                    <div style={{ height: 8, background: '#f3f4f6', borderRadius: 4, overflow: 'hidden' }}>
                      <div style={{
                        width: `${pct}%`, height: '100%', background: c, borderRadius: 4,
                        transition: 'width 1s cubic-bezier(0.4, 0, 0.2, 1)',
                      }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
      
      {/* Revenue By Source (If available) */}
      {dash.revenueBySrc && dash.revenueBySrc.length > 0 && (
        <div className="hover-card" style={{ padding: 32 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
            <div>
              <h3 style={{ fontFamily: 'var(--font-serif)', fontSize: 20, fontWeight: 700, color: CHAR, margin: 0 }}>
                Top Revenue Drivers
              </h3>
              <p style={{ fontSize: 13, color: MUTED, marginTop: 4, fontWeight: 500 }}>Monthly revenue grouped by demand/item source</p>
            </div>
            <div style={{ fontSize: 24, opacity: 0.2 }}>🎯</div>
          </div>
          
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16 }}>
            {dash.revenueBySrc.slice(0, 9).map((src, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 16, background: '#f9fafb', borderRadius: 8, border: `1px solid ${BORDER}`, transition: 'all 0.2s ease', cursor: 'pointer' }} onMouseEnter={e => {e.currentTarget.style.borderColor = GREEN; e.currentTarget.style.background = '#f0fdf4'}} onMouseLeave={e => {e.currentTarget.style.borderColor = BORDER; e.currentTarget.style.background = '#f9fafb'}}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ width: 40, height: 40, borderRadius: '50%', background: `${GREEN}1a`, color: GREEN, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', fontSize: 14 }}>
                    #{i+1}
                  </div>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: CHAR, maxWidth: 180, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={src.source}>
                      {src.source}
                    </div>
                    <div style={{ fontSize: 11, color: MUTED, marginTop: 2 }}>
                      Last: {new Date(src.lastDate).toLocaleDateString()}
                    </div>
                  </div>
                </div>
                <div style={{ fontSize: 16, fontWeight: 700, color: CHAR }}>
                  ₹{src.amount.toLocaleString('en-IN')}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
